import React, { useContext, useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  ContentLayout,
  Header,
  SpaceBetween,
} from "@amzn/awsui-components-console";
import "src/index.scss";
import { RegionalDomain } from "src/config/BartLambdaConfig";
import {
  ListArdsCommand,
  Ard,
} from "@amzn/fuse-accounting-central-datastore-client";
import ArdTableDisplay from "src/components/jemGenPage/ard-table-display";
import { InitDomain } from "src/context/domain/domainContext";
import { useJemGen } from "src/context/jem-gen/jemGenContext";
import { isProd } from "src/util";
import { useFlareContext } from "src/context/flare/flareContext";
import { getAllFusePaths } from "@amzn/fuse-flare-common";
import { FlareReleaseDomain } from "@amzn/fuse-ard-service-v2-client";

export type LoadingStatus = "loading" | "finished" | "error" | "unauthorized";

export const JemGenContent: React.FC = () => {
  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>("loading");

  // Use context for FACD clients
  const { getFacdClient, setExistingArds, setArdRegionMap } = useJemGen();

  // Use Flare context to fetch configs
  const { getFlareConfig } = useFlareContext();

  const { authService } = useContext(InitDomain);

  useEffect(() => {
    const fetchData = async () => {
      // Run authentication
      await authService.authenticate();

      // Load ARD data by default on component mount
      if (
        authService.getIsLoggedIn() &&
        (authService.ldap.includes("bart-jemgen-admin") ||
          authService.ldap.includes("bart-read-only") ||
          authService.ldap.includes("bart-admin"))
      ) {
        listArds();
        fetchFlareConfig();
      } else {
        setLoadingStatus("unauthorized");
      }
    };
    fetchData();
  }, []);

  const fetchFlareConfig = async () => {
    // Fetch Flare configs for JemGen
    try {
      console.log("Fetching Flare configs for JemGen...");

      // Fetch configs for all three domains
      const domains: FlareReleaseDomain[] = [
        FlareReleaseDomain.PROD,
        FlareReleaseDomain.DEVO,
        FlareReleaseDomain.UAT,
      ];

      // Get all Fuse mapping paths including processing_test
      const paths = getAllFusePaths();

      // Fetch configs with proper domains and paths
      await getFlareConfig(domains, paths);
    } catch (error) {
      console.error("Error fetching Flare configs:", error);
    }
  };

  const listArds = async () => {
    setLoadingStatus("loading");
    try {
      let allArds: Ard[] = [];
      let facdDomainList: RegionalDomain[];
      const newArdRegionMap = new Map();

      // Determine which regions to fetch from based on current domain
      if (!isProd()) {
        facdDomainList = ["devo-FE"];
      } else {
        facdDomainList = ["prod-FE", "prod-US", "prod-EU"];
      }

      // Fetch ARDs from all regions in parallel, allowing partial results on failure
      const results = await Promise.allSettled(
        facdDomainList.map(async (currentDomain) => {
          // Get FACD client using the new getFacdClient function
          const facdClient = await getFacdClient(currentDomain);
          if (!facdClient) {
            throw new Error(
              `Failed to get FACD client for domain '${currentDomain}'`,
            );
          }

          const listArdsCommand = new ListArdsCommand({
            withAssociatedPlans: true,
          });
          console.log(`Starting FACD listArds operation for ${currentDomain}`);

          const response = await facdClient.send(listArdsCommand);
          const regionArds: Ard[] =
            response.ards && Array.isArray(response.ards)
              ? (response.ards as Ard[])
              : [];
          return { domain: currentDomain, ards: regionArds };
        }),
      );

      // Process results - collect successful ARDs and log failures
      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          const { domain, ards: regionArds } = result.value;
          allArds = [...allArds, ...regionArds];
          // Collect ARD ID to region mappings
          regionArds.forEach((ard) => {
            if (ard.id) {
              newArdRegionMap.set(ard.id, domain);
            }
          });
        } else {
          console.error(
            `Error fetching ARDs from ${facdDomainList[index]}:`,
            result.reason,
          );
        }
      });

      // Update the ARD region map once after all regions are processed
      setArdRegionMap(newArdRegionMap);

      setExistingArds(allArds);
      setLoadingStatus("finished");
    } catch (error) {
      console.error("An error occurred in listArds:", error);
      setLoadingStatus("error");
    } finally {
      console.log("ListArds operation completed.");
    }
  };

  if (loadingStatus === "unauthorized") {
    return (
      <div>
        <Box margin="xxl">
          <Alert header="Access denied!" type="error"></Alert>
        </Box>
      </div>
    );
  }

  return (
    <ContentLayout
      header={
        <Header variant="h1">{`JemGen${!isProd() ? " (Beta)" : ""}`}</Header>
      }
    >
      <Container>
        <SpaceBetween direction="vertical" size="m">
          {loadingStatus === "error" && (
            <Alert
              type="error"
              header="Error loading ARDs"
              action={
                <Button
                  onClick={() => {
                    listArds();
                    fetchFlareConfig();
                  }}
                >
                  Reload
                </Button>
              }
            >
              There was an error loading the ARD data. Please try refreshing the
              page or contact support if the problem persists.
            </Alert>
          )}
          <ArdTableDisplay
            loadingStatus={loadingStatus}
            onRefresh={async () => {
              listArds();
              fetchFlareConfig();
            }}
          />
        </SpaceBetween>
      </Container>
    </ContentLayout>
  );
};

export default JemGenContent;
